/** DD-LINE-ID-001 CR-001 — prefer server line_display_id / line_ordinal over filtered index. */

import type { Request, Single, Subject } from '@/lib/dd-queue/types'
import type { SubjectMutation } from '@/lib/dd-queue/types/subjectMutation'
import {
  formatLineDisplayId,
  lineDisplayIdForRequest,
  totalLineCountForRequest,
} from '@/lib/dd-queue/lineDisplayId'
import { readSubjectMutation, subjectMutationKey } from '@/lib/dd-queue/subjectMutationKey'

export function subjectLineDisplayId(args: {
  requestId: string
  lineKey: string
  mutations: Record<string, SubjectMutation>
  totalLineCount: number
  /** Fallback only when mutation lacks ordinal / display id. Never recover index from SKU. */
  indexZeroBased?: number
}): string {
  const mut = readSubjectMutation(args.mutations, args.requestId, args.lineKey)
  if (typeof mut?.lineDisplayId === 'string' && mut.lineDisplayId.trim() !== '') {
    return mut.lineDisplayId
  }
  if (mut?.lineOrdinal != null && mut.lineOrdinal > 0) {
    return formatLineDisplayId(args.requestId, mut.lineOrdinal, args.totalLineCount)
  }
  return lineDisplayIdForRequest({
    requestId: args.requestId,
    indexZeroBased: args.indexZeroBased ?? 0,
    totalLineCount: args.totalLineCount,
  })
}

export function peekLineSubtitleLabel(args: {
  req: Request
  lineKey: string
  seed: Subject | Single
  mutations: Record<string, SubjectMutation>
  indexZeroBased: number
}): string {
  const refSku = args.seed.spec?.reference_sku?.sku?.trim()
  if (refSku) return refSku
  if (args.seed.sku && args.seed.sku !== args.lineKey) return args.seed.sku
  return subjectLineDisplayId({
    requestId: args.req.id,
    lineKey: args.lineKey,
    mutations: args.mutations,
    indexZeroBased: args.indexZeroBased,
    totalLineCount: totalLineCountForRequest(args.req),
  })
}

/** Bulk / dashboard row key — master lines keyed by lineKey; singles by request id. */
export function bulkSelectKey(req: Request, lineKey: string): string {
  return req.isMaster ? subjectMutationKey(req.id, lineKey) : req.id
}

export function lineKeyFromBulkSelectKey(req: Request, key: string): string | null {
  if (!req.isMaster) return (req as Single).lineKey ?? null
  const prefix = `${req.id}:`
  if (!key.startsWith(prefix)) return null
  return key.slice(prefix.length)
}
